import React, { useContext, useState } from 'react';
import { View, Pressable, Switch } from 'react-native';
import { useMutation } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';

import ClientContext from 'app/src/contexts/ClientContext';
import { Loading } from 'app/src/elements/DataState';
import mobileTable from 'app/src/elements/mobileTable';
import RelayTable from 'app/src/elements/RelayTable';
import { Text, Title, Setting, SettingLabel } from 'app/src/styles';
import colors from 'app/src/styles/colors';
import { Actions, Action, ActionText, CellText } from 'app/src/styles/table';
import { dateShort } from 'app/src/utils/formatting';
import { Link } from 'app/src/utils/routing';

import NewAccessToken from './new';
import EditAccessToken from './edit';
import MobileRow from './MobileRow';
import Token from './Token';
import { ACCESS_TOKENS, INACTIVATE_ACCESS_TOKEN } from './queries';

const AccessTokens = () => {
  const { currentUser, isMobile } = useContext(ClientContext);
  const [showNew, setShowNew] = useState();
  const [showEdit, setShowEdit] = useState();
  const [refetch, setRefetch] = useState();
  const [scopes, setScopes] = useState(['active']);

  const queryVariables = { scopes };

  const [inactivateAccessToken] = useMutation(INACTIVATE_ACCESS_TOKEN, {
    onCompleted: () => refetch(),
  });

  const handleInactivate = accessToken => {
    const confirmation =
      `Really make token "${accessToken.token.substr(0, 5)}..." inactive?` +
      ` This cannot be undone.`;

    if (window.confirm(confirmation)) {
      inactivateAccessToken({ variables: { id: accessToken.id } });
    }
  };

  const canAdmin =
    currentUser.userDealers.filter(userDealer => userDealer.admin).length > 0;

  let columns = [
    {
      key: 'dealer',
      label: 'Dealer',
      value: ({ value }) => value.label,
      content: ({ entry, value }) => (
        <Link to={`/dealers/${entry.dealer.id}`}>
          <CellText>{value}</CellText>
        </Link>
      )
    },
    {
      key: 'name',
      label: 'Name',
      content: ({ value, entry }) => (
        <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
          <CellText>{value}</CellText>
          {canAdmin && (
            <Pressable onPress={() => setShowEdit(entry)}>
              <FontAwesomeIcon icon={faPenToSquare} color="green" size={16} />
            </Pressable>
          )}
        </View>
      )
    },
    {
      key: 'token',
      label: 'Token',
      content: ({ entry }) => (
        <Token accessToken={entry} />
      )
    },
    {
      key: 'createdAt',
      label: 'Created',
      content: dateShort,
    },
  ];

  if (scopes.includes('inactive')) {
    columns.push({
      key: 'inactiveAt',
      label: 'Inactivated',
      content: dateShort,
    });
  }

  if (scopes.includes('active')) {
    columns.push({
      key: 'actions',
      label: 'Actions',
      sort: 'prevent',
      content: ({ entry }) => (
        entry.inactiveAt ? null : (
          <Pressable onPress={() => handleInactivate(entry)}>
            <ActionText>Make Inactive</ActionText>
          </Pressable>
        )
      )
    });
  }

  return (
    <View>
      <Title>API Access Tokens</Title>

      {canAdmin && (
        <View style={{ marginBottom: 10 }}>
          <Pressable onPress={() => setShowNew(true)}>
            <Text>Generate API Token</Text>
          </Pressable>
        </View>
      )}

      <Setting>
        <SettingLabel>Show deactivated tokens</SettingLabel>
        <Switch
          value={!scopes.includes('active')}
          onValueChange={value => setScopes(value ? ['inactive'] : ['active'])}
          activeTrackColor={colors.lightButton}
          activeThumbColor={colors.button}
        />
      </Setting>

      <RelayTable
        query={ACCESS_TOKENS}
        queryName="accessTokens"
        queryVariables={queryVariables}
        columns={columns}
        pageLength={25}
        setRefetch={setRefetch}
        component={isMobile ? mobileTable : null}
        componentProps={{ rowComponent: MobileRow }}
      />

      {showNew && <NewAccessToken setShow={setShowNew} refetch={refetch} />}

      {showEdit && (
        <EditAccessToken
          accessToken={showEdit}
          setShow={setShowEdit}
          refetch={refetch}
        />
      )}
    </View>
  );
};

export default AccessTokens;
